import { c as _c } from "react/compiler-runtime";
import { CommentAttachmentFile, PendingAttachment } from '@src/components/sco/Comments/CommentAttachments/types';
import { checkFileSize, checkValidExtension, uploadDocumentFile } from '@src/components/sco/UploadDocumentButton';
import { useCallback, useMemo, useState } from 'react';
const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_SIZE = 40000000;
function mapInitialFiles(files?: Array<CommentAttachmentFile | null | undefined>): PendingAttachment[] {
  return (files || []).filter((f): f is CommentAttachmentFile => !!f && !f.deletedAt && !!f.storedFileId).map(f => ({
    localKey: f.storedFileId as string,
    storedFileId: f.storedFileId as string,
    title: f.title || f.storageFileName || 'Attachment',
    fullUrl: f.fullUrl || undefined,
    mimeType: f.mimeType || undefined,
    isUploading: false
  }));
}
export function useCommentAttachments(options) {
  const $ = _c(23);
  const maxFiles = options?.maxFiles ?? DEFAULT_MAX_FILES;
  const maxSizeInBytes = options?.maxSizeInBytes ?? DEFAULT_MAX_SIZE;
  let t0;
  if ($[0] !== options?.allowedExtensions) {
    t0 = options?.allowedExtensions ?? ["*"];
    $[0] = options?.allowedExtensions;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const allowedExtensions = t0;
  const bypassAuth = options?.bypassAuth ?? false;
  const onError = options?.onError || _temp;
  let t1;
  if ($[2] !== options?.initialFiles) {
    t1 = () => mapInitialFiles(options?.initialFiles);
    $[2] = options?.initialFiles;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [pendingFiles, setPendingFiles] = useState(t1);
  let t2;
  if ($[4] !== pendingFiles) {
    t2 = pendingFiles.some(_temp2);
    $[4] = pendingFiles;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isUploading = t2;
  const isFull = pendingFiles.length >= maxFiles;
  let t3;
  if ($[6] !== allowedExtensions || $[7] !== bypassAuth || $[8] !== maxFiles || $[9] !== maxSizeInBytes || $[10] !== onError || $[11] !== pendingFiles.length) {
    t3 = async file => {
      if (pendingFiles.length >= maxFiles) {
        onError(`You can attach at most ${maxFiles} files.`);
        return;
      }
      if (!checkValidExtension({
        allowedExtensions,
        fileToUpload: file
      })) {
        onError(`Please select one of these file types: ${allowedExtensions.join(", ")}`);
        return;
      }
      if (!checkFileSize({
        fileToUpload: file,
        maxSizeInBytes
      })) {
        onError(`Please select a file that is less than ${maxSizeInBytes / 1000000}MB in size.`);
        return;
      }
      const localKey = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const optimistic = {
        localKey,
        storedFileId: "",
        title: file.name,
        mimeType: file.type,
        isUploading: true
      };
      setPendingFiles(prev => [...prev, optimistic]);
      await uploadDocumentFile({
        file,
        storedFile: {
          attachmentType: null,
          attachmentId: null,
          contextKey: null
        },
        maxSizeInBytes,
        bypassAuth,
        onSuccess: (url, storedFileId) => {
          setPendingFiles(prev_0 => prev_0.map(p => p.localKey === localKey ? {
            ...p,
            storedFileId: storedFileId || "",
            fullUrl: url,
            isUploading: false
          } : p));
        },
        onError: err => {
          setPendingFiles(prev_1 => prev_1.filter(p_0 => p_0.localKey !== localKey));
          onError(err?.toString?.() || "Upload failed.");
        }
      });
    };
    $[6] = allowedExtensions;
    $[7] = bypassAuth;
    $[8] = maxFiles;
    $[9] = maxSizeInBytes;
    $[10] = onError;
    $[11] = pendingFiles.length;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const addFile = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = localKeyOrStoredFileId => {
      setPendingFiles(prev_2 => prev_2.filter(p_1 => p_1.localKey !== localKeyOrStoredFileId && p_1.storedFileId !== localKeyOrStoredFileId));
    };
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const removeFile = t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setPendingFiles([]);
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const reset = t5;
  let t6;
  if ($[15] !== pendingFiles) {
    t6 = pendingFiles.filter(_temp3).map(_temp4);
    $[15] = pendingFiles;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const storedFileIds = t6;
  let t7;
  if ($[17] !== addFile || $[18] !== isFull || $[19] !== isUploading || $[20] !== pendingFiles || $[21] !== storedFileIds) {
    t7 = {
      pendingFiles,
      addFile,
      removeFile,
      reset,
      isUploading,
      isFull,
      storedFileIds,
      setPendingFiles
    };
    $[17] = addFile;
    $[18] = isFull;
    $[19] = isUploading;
    $[20] = pendingFiles;
    $[21] = storedFileIds;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}

/** Shared GraphQL selection — keep args identical everywhere for Apollo cache */
function _temp4(f_1) {
  return f_1.storedFileId;
}
function _temp3(f_0) {
  return f_0.storedFileId && !f_0.isUploading;
}
function _temp2(f) {
  return f.isUploading;
}
function _temp(msg) {
  return alert(msg);
}
export const COMMENT_STORED_FILES_SELECTION = `
  storedFiles(pageSize: 10, order: [["createdAt", "asc"]]) {
    items {
      id
      storedFileId
      title
      fullUrl
      mimeType
      deletedAt
    }
  }
`;